import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { DropdownSelectorPoint } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorPoint/DropdownSelectorPoint'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { selectedRegionHasPermission } from 'App/Map/PanelDetails/_utils/selectedRegionHasPermission'
import type { TLngLat, TValuesTimeseriesRegions } from 'App/Map/types'
import { lineChartTooltipFormatter } from 'components/StackedChart/_utils/lineChartTooltipFormatter/lineChartTooltipFormatter'
import type { StackedChartSection, TChartSeries, TValueTypeObject } from 'components/StackedChart/types'
import { isEmpty } from 'lodash'
import moment from 'moment-timezone'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type {
  TPointCategory,
  TSelectedFieldAssetsStoreState,
} from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { isRegionDataEnabledForCharts } from 'utils/useIsRegionDataEnabled'
import { chooseAmongstUnAggHourlyAndDaily } from '../../chooseAmongstUnAggHourlyAndDaily'
import { getColorRulesForCanopySeries } from '../../createContent/_utils/getColorRulesForSeries'
import { generateSeriesForEnvironmentalChart } from '../_utils/generateSeriesForEnvironmentalChart/generateSeriesForEnvironmentalChart'
import { getXDateFormat } from '../_utils/getXDateFormat'
import {
  makeRegionalSeries,
  makeRegionalSeriesFromRegularSeries,
} from '../_utils/makeRegionalSeriesFromRegularSeries'
import { plotOptionsToHideInactiveSeriesHarderMaker } from '../_utils/plotOptionsToHideInactiveSeriesHarderMaker'
import { updateTooltipForValueSource } from '../_utils/tooltipOptionsForValueSourceCharts'

const pointCategory: TPointCategory = 'weatherPoint'
const permissionRequired = 'VIEW_WEATHER_DETAILS'

const checkPermission = () =>
  selectedPropertyHasPermission({ permission: permissionRequired }) ||
  (isUserOnlyAFreeRegionalUser() && selectedRegionHasPermission({ permission: permissionRequired }))

const possibleValueTypesToRequest: VV.DomainTypes.Weather.TTimeseriesValueTypeKeysMerged[] = [
  'dewPoint_ABOVE',
  'dewPoint_BELOW',
  'dewPoint_IN',
]

const getRegionalValuesRequested = ({
  selectedFieldAssets,
  preferredAggregationInterval,
}: {
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  preferredAggregationInterval: {
    preferredAggregationInterval: ReturnType<typeof chooseAmongstUnAggHourlyAndDaily>
  }
}): Partial<routes.Values.Request> => {
  const selectedRegion = selectedFieldAssets.region

  if (!selectedRegion) return {}

  const valuesForRegion = fieldAssetStore.getState().regions?.[selectedRegion]?.valuesTimeseries || []

  const valuesToRequestForRegion = possibleValueTypesToRequest.filter((v) =>
    valuesForRegion.includes(v as TValuesTimeseriesRegions),
  )

  const valuesRequested = valuesToRequestForRegion.reduce(
    (request, valueType) => {
      request[valueType] = preferredAggregationInterval

      return request
    },
    {} as Partial<Record<(typeof possibleValueTypesToRequest)[number], typeof preferredAggregationInterval>>,
  )

  if (isEmpty(valuesRequested)) return {}

  return {
    regions: {
      regionIds: [selectedRegion],
      valuesRequested,
    },
  }
}

const getPointsValuesRequested = ({
  selectedFieldAssets,
  preferredAggregationInterval,
}: {
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  preferredAggregationInterval: {
    preferredAggregationInterval: ReturnType<typeof chooseAmongstUnAggHourlyAndDaily>
  }
}): Partial<routes.Values.Request> => {
  if (!selectedFieldAssets[pointCategory] || !selectedFieldAssets.property) return {}

  const valuesForPoint =
    fieldAssetStore.getState().properties?.[selectedFieldAssets.property]?.points?.[
      selectedFieldAssets[pointCategory]
    ]?.valuesTimeseries || []

  const valuesToRequest = possibleValueTypesToRequest.filter((v) => valuesForPoint.includes(v))

  const valuesRequested = valuesToRequest.reduce(
    (request, valueType) => {
      request[valueType] = preferredAggregationInterval

      return request
    },
    {} as Partial<Record<(typeof possibleValueTypesToRequest)[number], typeof preferredAggregationInterval>>,
  )

  if (isEmpty(valuesRequested)) return {}

  return {
    points: {
      lngLats: [selectedFieldAssets[pointCategory]],
      valuesRequested,
    },
  }
}

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!checkPermission()) return {}

  if (!selectedValueGroups.dew_point) return {}

  const preferredAggregationInterval = {
    preferredAggregationInterval: chooseAmongstUnAggHourlyAndDaily(),
  }

  const pointsToRequest = getPointsValuesRequested({
    selectedFieldAssets,
    preferredAggregationInterval,
  })

  const showRegionalData = isRegionDataEnabledForCharts()

  const regionsToRequest = showRegionalData
    ? getRegionalValuesRequested({
        selectedFieldAssets,
        preferredAggregationInterval,
      })
    : {}

  return { ...pointsToRequest, ...regionsToRequest }
}

export const content = ({
  data,
  compareSeasonsData,
}: {
  data: routes.Values.Response
  compareSeasonsData: routes.Values.Response
}): StackedChartSection => {
  const commonReturnItems = {
    title: unitConverter.dewPointInCanopy().categoryTitleWithoutUnit(),
    fieldAssetPicker: (
      <DropdownSelectorPoint
        pointCategory={pointCategory}
        valuesTimeseriesToFilterOn={possibleValueTypesToRequest}
      />
    ),
    id: 'stackem-dew-point',
  }

  if (!checkPermission()) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const showRegionalData = isRegionDataEnabledForCharts()
  const selectedFieldAssets = selectedFieldAssetsStore.getState()
  const stationLngLat = String(selectedFieldAssets[pointCategory])
  const selectedRegion = selectedFieldAssets.region
  const series: TChartSeries[] = []
  const stationValues = data?.points?.[stationLngLat]?.values
  const regionValues = data?.regions?.[String(selectedRegion)]?.values
  const hasAboveCanopySensor = !isEmpty(stationValues?.dewPoint_ABOVE?.[0]?.timeseries)
  const hasInCanopySensor = !isEmpty(stationValues?.dewPoint_IN?.[0]?.timeseries)
  const hasBelowCanopySensor = !isEmpty(stationValues?.dewPoint_BELOW?.[0]?.timeseries)
  const hasRegion = showRegionalData && !!selectedRegion

  const colorRules = getColorRulesForCanopySeries({
    hasAboveCanopySensor,
    hasInCanopySensor,
    hasBelowCanopySensor,
    hasRegion,
  })

  const isVirtual =
    fieldAssetStore.getState().properties?.[Number(selectedFieldAssets.property)]?.points?.[
      stationLngLat as TLngLat
    ]?.isVirtual

  const isDaily =
    stationValues?.dewPoint_ABOVE?.[0]?.metadata?.aggregationInterval === 'DAILY' ||
    stationValues?.dewPoint_BELOW?.[0]?.metadata?.aggregationInterval === 'DAILY' ||
    stationValues?.dewPoint_IN?.[0]?.metadata?.aggregationInterval === 'DAILY' ||
    regionValues?.dewPoint_IN?.[0]?.metadata?.aggregationInterval === 'DAILY'

  const aboveCanopySeries = generateSeriesForEnvironmentalChart({
    compareSeasonsValueTypeObject: compareSeasonsData?.points?.[stationLngLat]?.values
      ?.dewPoint_ABOVE?.[0] as TValueTypeObject,
    hasSensor: hasAboveCanopySensor,
    unitConverter: unitConverter.dewPointAboveCanopy,
    valueTypeObject: stationValues?.dewPoint_ABOVE?.[0] as TValueTypeObject,
    color: colorRules.above[0],
    compareSeasonsColor: colorRules.above[1],
  })

  const inCanopySeries = generateSeriesForEnvironmentalChart({
    compareSeasonsValueTypeObject: compareSeasonsData?.points?.[stationLngLat]?.values
      ?.dewPoint_IN?.[0] as TValueTypeObject,
    hasSensor: hasInCanopySensor,
    unitConverter: unitConverter.dewPointInCanopy,
    valueTypeObject: stationValues?.dewPoint_IN?.[0] as TValueTypeObject | undefined,
    color: colorRules.in[0],
    compareSeasonsColor: colorRules.in[1],
  })

  const belowCanopySeries = generateSeriesForEnvironmentalChart({
    compareSeasonsValueTypeObject: compareSeasonsData?.points?.[stationLngLat]?.values
      ?.dewPoint_BELOW?.[0] as TValueTypeObject,
    hasSensor: hasBelowCanopySensor,
    unitConverter: unitConverter.dewPointBelowCanopy,
    valueTypeObject: stationValues?.dewPoint_BELOW?.[0] as TValueTypeObject | undefined,
    color: colorRules.below[0],
    compareSeasonsColor: colorRules.below[1],
  })

  series.push(...aboveCanopySeries, ...inCanopySeries, ...belowCanopySeries)

  let regionSeriesId: string

  if (showRegionalData && selectedRegion) {
    const regionData = data.regions?.[selectedRegion]?.values?.dewPoint_IN?.[0]
    const regionName = selectedRegion ? fieldAssetStore.getState().regions?.[selectedRegion]?.name : ''

    if (series.length) {
      const baseSeries = inCanopySeries[0] || aboveCanopySeries[0] || belowCanopySeries[0]

      const shapedData = (regionData?.timeseries || []).map((ts) => ({
        x: +new Date(ts.timestamp),
        y: unitConverter
          .dewPointInCanopy(typeof ts.value === 'object' ? ts.value?.average : ts.value)
          .value(),
        low: isDaily
          ? unitConverter.dewPointInCanopy(typeof ts.value === 'object' ? ts.value?.min : null).value()
          : undefined,
        high: isDaily
          ? unitConverter.dewPointInCanopy(typeof ts.value === 'object' ? ts.value?.max : null).value()
          : undefined,
      }))

      const regionalSeries = makeRegionalSeriesFromRegularSeries(baseSeries, {
        name: regionName,
        data: shapedData,
        color: colorRules.region,
      })

      regionSeriesId = regionalSeries.id

      series.push(regionalSeries)
    } else {
      const seriesName = selectedFieldAssets[pointCategory] ? regionName : undefined

      const regionalSeries = generateSeriesForEnvironmentalChart({
        hasSensor: true,
        valueTypeObject: regionData as TValueTypeObject,
        unitConverter: unitConverter.dewPointInCanopy,
        overrideSeriesName: seriesName,
        color: colorRules.region,
      })

      regionSeriesId = regionalSeries[0]?.id

      regionalSeries.forEach((s) => series.push(makeRegionalSeries(s)))
    }
  }

  const timezone = getTimezoneForSelectedPropertyOrRegion()

  const allForecastMoments = [
    stationValues?.dewPoint_ABOVE?.[0]?.metadata.forecastStartsAt,
    stationValues?.dewPoint_IN?.[0]?.metadata.forecastStartsAt,
    stationValues?.dewPoint_BELOW?.[0]?.metadata.forecastStartsAt,
    selectedRegion
      ? data.regions?.[selectedRegion]?.values?.dewPoint_IN?.[0]?.metadata.forecastStartsAt
      : null,
  ]
    .filter(Boolean)
    .map((d) => moment.tz(d, timezone))

  const firstForecastTimestamp = allForecastMoments.length ? +moment.min(allForecastMoments) : +new Date()

  return {
    ...commonReturnItems,
    items: [
      {
        chartConfig: {
          semiosHighchartsAdditions: {
            id: commonReturnItems.id,
            firstForecastTimestamp,
          },
          chart: {
            type: 'line',
          },
          plotOptions: plotOptionsToHideInactiveSeriesHarderMaker(),
          series,

          tooltip: {
            xDateFormat: getXDateFormat(isDaily),
            formatter: function (tooltip) {
              let tooltipContents = lineChartTooltipFormatter(this, tooltip, firstForecastTimestamp)

              tooltipContents = updateTooltipForValueSource({
                seriesIds: [
                  aboveCanopySeries[0]?.id,
                  inCanopySeries[0]?.id,
                  belowCanopySeries[0]?.id,
                  regionSeriesId,
                ].filter(Boolean),
                regionSeriesIds: [regionSeriesId].filter(Boolean),
                compareSeasonIds: [
                  aboveCanopySeries[1]?.id,
                  inCanopySeries[1]?.id,
                  belowCanopySeries[1]?.id,
                ].filter(Boolean),
                points: this.points,
                tooltipContents,
                firstForecastTimestamp,
                isVirtual,
              })

              return tooltipContents
            },
          },
        },
      },
    ],
  }
}
